import { useState, useEffect, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import {
  useSEOMeta,
  generateArticleSchema,
  generateBreadcrumbSchema,
} from "@/components/SEO";

const categories = ["All", "Health", "Education", "Protection", "Economic Security", "General"];

export default function ArticleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [article, setArticle] = useState<any>(null);
  const [relatedArticles, setRelatedArticles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useSEOMeta({
    title: article?.title
      ? `${article.title} - BHODEV News`
      : "Article - BHODEV Cameroon",
    description:
      article?.excerpt ||
      "Read the latest news and stories from Bogi Health Organization for Development (BHODEV) Cameroon.",
    keywords: `${article?.category || "News"}, BHODEV ${article?.category || ""}, Cameroon NGO, humanitarian news, community health Cameroon, ${article?.title || ""}`,
    canonicalUrl: article?.id
      ? `https://bhodevcameroon.org/news/${article.id}`
      : undefined,
    ogImage: article?.image_url || undefined,
    ogType: "article",
    schemaData: article
      ? {
          ...generateArticleSchema(
            article.title,
            article.excerpt || "",
            `https://bhodevcameroon.org/news/${article.id}`,
            article.image_url || "",
            article.date,
            article.author
          ),
          ...generateBreadcrumbSchema([
            { name: "Home", url: "https://bhodevcameroon.org/" },
            { name: "News", url: "https://bhodevcameroon.org/news" },
            {
              name: article.title,
              url: `https://bhodevcameroon.org/news/${article.id}`,
            },
          ]),
        }
      : undefined,
  });

  const fetchArticle = useCallback(async () => {
    if (!id) return;

    // Validate UUID format before querying
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(id)) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    setLoading(true);
    const { data, error } = await supabase
      .from("articles")
      .select("*")
      .eq("id", id)
      .maybeSingle();

    if (error || !data) {
      console.error("Error fetching article:", error);
      setNotFound(true);
      setLoading(false);
      return;
    }

    setArticle(data);

    // Fetch related articles in same category
    const { data: related } = await supabase
      .from("articles")
      .select("*")
      .eq("category", data.category)
      .eq("status", "Published")
      .neq("id", id)
      .order("date", { ascending: false })
      .limit(3);

    setRelatedArticles(related || []);
    setLoading(false);
  }, [id]);

  useEffect(() => {
    fetchArticle();
  }, [fetchArticle]);

  const formatDate = (d: string) => {
    if (!d) return "";
    return new Date(d).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const shareArticle = () => {
    if (navigator.share) {
      navigator.share({
        title: article?.title,
        text: article?.excerpt,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      const el = document.getElementById("share-toast");
      if (el) {
        el.classList.remove("opacity-0", "translate-y-2");
        el.classList.add("opacity-100", "translate-y-0");
        setTimeout(() => {
          el.classList.add("opacity-0", "translate-y-2");
          el.classList.remove("opacity-100", "translate-y-0");
        }, 2000);
      }
    }
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center px-4">
            <div className="w-16 h-16 flex items-center justify-center bg-stone-100 rounded-full mx-auto mb-4">
              <i className="ri-article-line text-2xl text-stone-400"></i>
            </div>
            <h1 className="text-2xl font-saria font-bold text-stone-800 mb-2">Article Not Found</h1>
            <p className="text-stone-500 mb-6">The article you are looking for does not exist or has been removed.</p>
            <Link
              to="/news"
              className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-full font-semibold text-sm hover:bg-primary-dark transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              Back to News
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Toast notification */}
      <div
        id="share-toast"
        className="fixed top-20 left-1/2 -translate-x-1/2 z-50 bg-stone-800 text-white px-4 py-2 rounded-xl text-sm font-medium opacity-0 translate-y-2 transition-all duration-300 pointer-events-none shadow-lg"
      >
        <i className="ri-check-line mr-1.5"></i>
        Link copied to clipboard
      </div>

      {loading && (
        <div className="flex items-center justify-center min-h-[60vh]">
          <i className="ri-loader-4-line animate-spin text-primary text-3xl"></i>
        </div>
      )}

      {!loading && article && (
        <>
          {/* Hero Cover */}
          <section className="relative h-[400px] md:h-[500px] flex items-end overflow-hidden">
            <img
              src={
                article.image_url ||
                "https://readdy.ai/api/search-image?query=Community%20development%20news%20and%20journalism%20in%20Cameroon%20vibrant%20storytelling%20atmosphere%20warm%20lighting%20simple%20clean%20background%20photorealistic%20style&width=1400&height=600&seq=article-detail-fallback&orientation=landscape"
              }
              alt={article.title}
              title={article.title}
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/20"></div>

            <div className="relative z-10 w-full max-w-5xl mx-auto px-4 md:px-8 pb-8 md:pb-12">
              <nav className="flex items-center gap-2 text-white/60 text-sm mb-4" aria-label="Breadcrumb">
                <Link to="/" className="hover:text-white transition-colors">Home</Link>
                <i className="ri-arrow-right-s-line"></i>
                <Link to="/news" className="hover:text-white transition-colors">News</Link>
                <i className="ri-arrow-right-s-line"></i>
                <span className="text-white/90 truncate max-w-[200px]">{article.title}</span>
              </nav>
              <span className="inline-block bg-white/20 backdrop-blur-sm text-white text-xs font-semibold px-3 py-1.5 rounded-full mb-4">
                {article.category}
              </span>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-saria font-bold text-white leading-tight mb-4">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                    <span className="text-xs text-white font-bold">
                      {article.author
                        .split(" ")
                        .map((n: string) => n[0])
                        .join("")}
                    </span>
                  </div>
                  <span className="text-sm text-white/90 font-medium">{article.author}</span>
                </div>
                <span className="text-white/50">|</span>
                <span className="text-sm text-white/70">{formatDate(article.date)}</span>
                {article.read_time && (
                  <>
                    <span className="text-white/50">|</span>
                    <span className="text-sm text-white/70">{article.read_time} read</span>
                  </>
                )}
              </div>
            </div>
          </section>

          {/* Article Content */}
          <section className="py-10 md:py-16 px-4 md:px-8 lg:px-12">
            <div className="max-w-4xl mx-auto">
              {/* Excerpt */}
              {article.excerpt && (
                <p className="text-lg md:text-xl text-stone-600 font-medium leading-relaxed mb-8 border-l-4 border-primary pl-5">
                  {article.excerpt}
                </p>
              )}

              {/* Full Content */}
              {article.content ? (
                <div
                  className="prose-content text-stone-700 text-base md:text-lg leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />
              ) : (
                <p className="text-stone-500 text-base italic">Full article content is being prepared.</p>
              )}

              {/* Share & Back */}
              <div className="flex items-center justify-between mt-12 pt-8 border-t border-stone-100">
                <Link
                  to="/news"
                  className="flex items-center gap-2 text-stone-500 hover:text-stone-700 text-sm font-medium transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i>
                  Back to News
                </Link>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-stone-400 mr-2">Share:</span>
                  <button
                    onClick={shareArticle}
                    className="w-10 h-10 flex items-center justify-center text-stone-400 hover:text-primary hover:bg-primary/10 rounded-full transition-all cursor-pointer"
                    title="Share article"
                  >
                    <i className="ri-share-line text-lg"></i>
                  </button>
                  <button
                    onClick={() => {
                      const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(article.title)}&url=${encodeURIComponent(window.location.href)}`;
                      window.open(url, "_blank");
                    }}
                    className="w-10 h-10 flex items-center justify-center text-stone-400 hover:text-stone-800 hover:bg-stone-100 rounded-full transition-all cursor-pointer"
                    title="Share on X"
                  >
                    <i className="ri-twitter-x-line text-lg"></i>
                  </button>
                  <button
                    onClick={() => {
                      const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`;
                      window.open(url, "_blank");
                    }}
                    className="w-10 h-10 flex items-center justify-center text-stone-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-all cursor-pointer"
                    title="Share on Facebook"
                  >
                    <i className="ri-facebook-fill text-lg"></i>
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="py-14 md:py-20 px-4 md:px-8 lg:px-12 bg-stone-50">
              <div className="max-w-6xl mx-auto">
                <h2 className="text-2xl md:text-3xl font-saria font-bold text-dark mb-10">
                  More in {article.category}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                  {relatedArticles.map((a) => (
                    <Link
                      key={a.id}
                      to={`/news/${a.id}`}
                      className="group bg-white rounded-2xl overflow-hidden cursor-pointer hover:shadow-xl transition-all duration-500 border border-stone-100"
                    >
                      <div className="relative h-[200px] overflow-hidden">
                        <img
                          src={
                            a.image_url ||
                            "https://readdy.ai/api/search-image?query=Community%20development%20news%20and%20journalism%20in%20Cameroon%20vibrant%20storytelling%20atmosphere%20warm%20lighting%20simple%20clean%20background%20photorealistic%20style&width=800&height=500&seq=related-fallback&orientation=landscape"
                          }
                          alt={a.title}
                          title={a.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                        />
                        <div className="absolute top-4 left-4">
                          <span className="bg-white/90 backdrop-blur-sm text-primary text-xs px-3 py-1.5 rounded-full font-inter font-semibold shadow-sm">
                            {a.category}
                          </span>
                        </div>
                      </div>
                      <div className="p-5">
                        <div className="flex items-center gap-3 mb-3">
                          <span className="text-xs text-stone-500">{formatDate(a.date)}</span>
                        </div>
                        <h3 className="text-base font-saria font-bold text-dark leading-snug mb-2 group-hover:text-primary transition-colors duration-300">
                          {a.title}
                        </h3>
                        <p className="text-sm text-stone-500 leading-relaxed line-clamp-2">{a.excerpt || ""}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </>
      )}

      <Footer />
    </div>
  );
}